import { ChangeEvent, FC, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { Box, Skeleton, Stack, Typography } from '@mui/material'

import { usePostPublicFilesMutation } from '../../../api/core'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { useSaveAnalysisScreenshotsMutation } from '../../api/analysis-screenshots'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog/UnsavedChangesDialog'
import { ScreenshotsHeader } from '../../features/analysis-screenshots/components/ScreenshotsHeader/ScreenshotsHeader'
import { UploadEntry, UploadFilesTable, UploadStatus } from '../../features/analysis-screenshots/components/UploadFilesTable/UploadFilesTable'
import { Notice, WriteNotice } from '../../features/analysis-screenshots/components/WriteNotice/WriteNotice'
import { toNewScreenshotItem } from '../../features/analysis-screenshots/helpers/screenshotHelpers'
import { getAnalysisScreenshotsPath } from '../../features/analysis-screenshots/helpers/screenshotPaths'
import { toScreenshotUploadFile } from '../../features/analysis-screenshots/helpers/uploadHelpers'
import { getScreenshotsReadBackProblems, isScreenshotsChangedError, toPositions } from '../../features/analysis-screenshots/helpers/writeMessages'
import { useAnalysisScreenshotsData } from '../../features/analysis-screenshots/hooks/useAnalysisScreenshotsData'
import { EventScreenshotsUploader } from '../../features/live-events-tracking/components/EventScreenshotsContainer/EventScreenshotsUploader/EventScreenshotsUploader'
import { MAX_PUBLIC_UPLOAD_FILE_SIZE_MB, partitionFilesByMaxSize } from '../../helpers/fileUploadLimits'
import { useEditPageLeave } from '../../hooks/useEditPageLeave'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'

// `postPublicFiles` retries each file; three files go up at a time, as it batches them.
const parallelUploads = 3

export const AnalysisScreenshotsUploadPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-analysis-screenshots-upload'))
  const { analysisId = '' } = useParams<{ analysisId: string }>()
  return <AnalysisScreenshotsUpload key={analysisId} analysisId={analysisId} />
}

const AnalysisScreenshotsUpload: FC<{ analysisId: string }> = ({ analysisId }) => {
  const { t } = useTranslation()
  const { notifySuccess } = useMutationFeedback()
  const navigate = useNavigate()
  const data = useAnalysisScreenshotsData(analysisId)
  const [uploadFiles] = usePostPublicFilesMutation()
  const [saveScreenshots] = useSaveAnalysisScreenshotsMutation()
  const [entries, setEntries] = useState<UploadEntry[]>([])
  const [isWorking, setIsWorking] = useState(false)
  const [notice, setNotice] = useState<Notice>()
  const nextKey = useRef(0)
  // Uploaded files can't be deleted, so leaving before they're added to the analysis asks first.
  const { guard, leave } = useEditPageLeave(isWorking || entries.some(({ status }) => status === 'uploaded'))

  const setStatus = (key: string, status: UploadStatus, upload?: UploadEntry['upload']) =>
    setEntries((current) => current.map((entry) => (entry.key === key ? { ...entry, status, upload: upload ?? entry.upload } : entry)))

  const handleFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const { accepted, rejected } = partitionFilesByMaxSize(Array.from(event.target.files ?? []))
    const toEntry = (file: File, status: UploadStatus): UploadEntry => ({ key: String(nextKey.current++), file, status })
    setEntries((current) => [...current, ...accepted.map((file) => toEntry(file, 'waiting')), ...rejected.map((file) => toEntry(file, 'too_big'))])
  }

  const uploadEntry = async (entry: UploadEntry) => {
    try {
      setStatus(entry.key, 'converting')
      const file = await toScreenshotUploadFile(entry.file)
      setStatus(entry.key, 'uploading')
      const [uploaded] = await uploadFiles({ files: [file], type: 'screenshot', route: 'public' }).unwrap()
      if (!uploaded?.id || !uploaded.url) throw new Error('The upload answered no file')
      setStatus(entry.key, 'uploaded', { id: uploaded.id, url: uploaded.url })
      return { ...entry, status: 'uploaded' as const, upload: { id: uploaded.id, url: uploaded.url } }
    } catch {
      setStatus(entry.key, 'failed')
      return { ...entry, status: 'failed' as const }
    }
  }

  const addToAnalysis = async (uploaded: UploadEntry[]) => {
    const newItems = uploaded.map((entry) => toNewScreenshotItem(entry.upload as { id: string; url: string }))
    const body = [...data.items, ...newItems]
    try {
      const { stored, readBack } = await saveScreenshots({ analysisId, loaded: data.items, body }).unwrap()
      const storedIds = new Set(stored.map(({ screenshotId }) => screenshotId))
      const added = uploaded.filter((entry) => storedIds.has(entry.upload?.id ?? ''))
      added.forEach((entry) => setStatus(entry.key, 'added'))
      const problems = getScreenshotsReadBackProblems(readBack, toPositions(body.map(({ screenshotId }) => screenshotId)))
      return { addedCount: added.length, problems }
    } catch (error) {
      return {
        addedCount: 0,
        problems: [
          {
            key: isScreenshotsChangedError(error)
              ? 'internal-analysts:analysis_screenshots_changed'
              : 'internal-analysts:analysis_screenshots_upload_save_failed',
          },
        ],
      }
    }
  }

  const handleStart = async () => {
    setIsWorking(true)
    setNotice(undefined)
    const queue = entries.filter(({ status }) => status === 'waiting' || status === 'failed')
    const results: UploadEntry[] = entries.filter(({ status }) => status === 'uploaded')
    const worker = async () => {
      for (let entry = queue.shift(); entry; entry = queue.shift()) results.push(await uploadEntry(entry))
    }
    await Promise.all(Array.from({ length: parallelUploads }, worker))
    const pickedOrder = new Map(entries.map(({ key }, index) => [key, index]))
    const uploaded = results.filter(({ status }) => status === 'uploaded').sort((a, b) => (pickedOrder.get(a.key) ?? 0) - (pickedOrder.get(b.key) ?? 0))
    const failedCount = results.filter(({ status }) => status === 'failed').length
    const { addedCount, problems } = uploaded.length ? await addToAnalysis(uploaded) : { addedCount: 0, problems: [] }
    setIsWorking(false)
    const messages = [
      ...(failedCount ? [{ key: 'internal-analysts:analysis_screenshots_upload_failed', options: { count: failedCount } }] : []),
      ...(uploaded.length > addedCount
        ? [{ key: 'internal-analysts:analysis_screenshots_upload_not_added', options: { count: uploaded.length - addedCount } }]
        : []),
      ...problems,
    ]
    if (messages.length) {
      setNotice({ severity: 'error', messages })
      return
    }
    notifySuccess(t('internal-analysts:analysis_screenshots_upload_added', { count: addedCount }))
    leave(getAnalysisScreenshotsPath(analysisId))
  }

  if (data.error) return <Typography color="error.main">{t(`internal-analysts:analysis_screenshots_load_failed_${data.error}`)}</Typography>
  if (data.isLoading || !data.analysis || !data.game) return <Skeleton variant="rectangular" height={320} />

  const startable = entries.filter(({ status }) => status === 'waiting' || status === 'failed' || status === 'uploaded')

  return (
    <Box pb={8}>
      <ScreenshotsHeader
        analysis={data.analysis}
        game={data.game}
        backLabel={t('internal-analysts:analysis_screenshots_back_to_list')}
        onBack={() => navigate(getAnalysisScreenshotsPath(analysisId))}
        detail={t('internal-analysts:analysis_overview_analysis_screenshots', { count: data.items.length })}
      />
      <WriteNotice notice={notice} onClose={() => setNotice(undefined)} />
      <Stack spacing={2}>
        <EventScreenshotsUploader files={startable.map(({ file }) => file)} onChange={handleFiles} onUpload={handleStart} isUploading={isWorking} />
        <Typography variant="body2" color="text.secondary">
          {t('internal-analysts:analysis_screenshots_upload_help', { maxSize: MAX_PUBLIC_UPLOAD_FILE_SIZE_MB })}
        </Typography>
        {!!entries.length && (
          <UploadFilesTable entries={entries} disabled={isWorking} onRemove={(key) => setEntries((current) => current.filter((entry) => entry.key !== key))} />
        )}
      </Stack>
      <UnsavedChangesDialog guard={guard} text={t('internal-analysts:analysis_screenshots_upload_leave_text')} />
    </Box>
  )
}
